import { useParams, useNavigate } from 'react-router-dom';

import {
  FullScreenLoading,
  TIER_TO_TRACK_CODE,
  EducationalInvestmentCode,
  EDUCATIONAL_INVESTMENT_CODES,
  HideOnRoute,
} from '@backrs_ui/common-ui';

import ExploreScholarships from '@backrs_ui/common-ui/src/assets/images/explore-scholarships.png';
import MajorMalfunction from '@backrs_ui/common-ui/src/assets/images/major-malfunction.svg';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useGetEducationalInvestmentsByCareerTrackQuery } from '@backrs_ui/grow-ui/src/redux/educationalInvestment/educationalInvestment.api';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { selectSharedBlueprintData } from '@backrs_ui/grow-ui/src/redux/sharedSession/sharedSession';

import { Box, Theme, SxProps } from '@mui/material';

import { EducationalInvestmentHeader } from './atoms/EducationalInvestmentHeader';
import { EducationalInvestmentSection } from './atoms/EducationalInvestmentSection';
import { useAgentContext } from './EducationalInvestmentPageContainer.agentContext';
import { hydrateEducationalInvestment } from './utils';
import { NotFound404Page } from '../NotFound404/NotFound404Page';

const SECTION_STRINGS = appStrings.modules.educationalInvestment.sections;

type EducationalInvestmentPageContainerProps = Readonly<{
  isShared?: boolean;
}>;

export const EducationalInvestmentPageContainer = ({
  isShared = false,
}: EducationalInvestmentPageContainerProps) => {
  const navigate = useNavigate();
  const { code } = useParams<{ code: string }>();
  const { results, loadingMostRecentUserResponseSet } = useAppSelector(
    (state) => state.userResponseSet,
  );

  const sharedBlueprintData = useAppSelector(selectSharedBlueprintData);

  const tierToUse = isShared && sharedBlueprintData ? sharedBlueprintData.tier : results?.tier;

  const careerTrackCode = tierToUse ? TIER_TO_TRACK_CODE[tierToUse] : undefined;

  const { data: educationalInvestmentsData, isLoading } =
    useGetEducationalInvestmentsByCareerTrackQuery({ careerTrackCode: careerTrackCode });

  const targetEducationalInvestments = (educationalInvestmentsData?.target || []).map(
    hydrateEducationalInvestment,
  );

  const otherEducationalInvestments = (educationalInvestmentsData?.others || []).map(
    hydrateEducationalInvestment,
  );

  useAgentContext(targetEducationalInvestments, otherEducationalInvestments);

  if (loadingMostRecentUserResponseSet || isLoading) {
    return <FullScreenLoading />;
  }

  if (
    code &&
    !EDUCATIONAL_INVESTMENT_CODES.includes(code.replace(/-/g, '_') as EducationalInvestmentCode)
  ) {
    return <NotFound404Page />;
  }

  return (
    <Box sx={containerSx}>
      <EducationalInvestmentHeader isShared={isShared} />
      <EducationalInvestmentSection
        title={SECTION_STRINGS.targetEducationalInvestments.title}
        description={SECTION_STRINGS.targetEducationalInvestments.description(
          targetEducationalInvestments.length,
        )}
        educationalInvestments={targetEducationalInvestments}
        hideCardActions={isShared}
      />
      <HideOnRoute route="shared">
        <EducationalInvestmentSection
          title={SECTION_STRINGS.educationTradeOff.title}
          description={SECTION_STRINGS.educationTradeOff.description}
          tradeOff={{
            description: [
              SECTION_STRINGS.educationTradeOff.content1,
              SECTION_STRINGS.educationTradeOff.content2,
            ],
            imageCards: [
              {
                imageUrl: MajorMalfunction,
                title: SECTION_STRINGS.educationTradeOff.imageCards.majorMalfunction.title,
                beta: true,
                cta: SECTION_STRINGS.educationTradeOff.imageCards.majorMalfunction.cta,
                description:
                  SECTION_STRINGS.educationTradeOff.imageCards.majorMalfunction.description,
                onClick: () => {
                  navigate(APP_ROUTE.MAJORS);
                },
              },
              {
                imageUrl: ExploreScholarships,
                title: SECTION_STRINGS.educationTradeOff.imageCards.findScholarships.title,
                cta: SECTION_STRINGS.educationTradeOff.imageCards.findScholarships.cta,
                description:
                  SECTION_STRINGS.educationTradeOff.imageCards.findScholarships.description,
                onClick: () => {
                  navigate(`${APP_ROUTE.SEARCH}/scholarships`);
                },
              },
            ],
          }}
        />
      </HideOnRoute>
      <EducationalInvestmentSection
        title={SECTION_STRINGS.otherEducationalInvestments.title}
        description={SECTION_STRINGS.otherEducationalInvestments.description}
        educationalInvestments={otherEducationalInvestments}
        hideCardActions={isShared}
      />
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflowY: 'auto',
  alignItems: 'left',
  containerType: 'inline-size',
  gap: { xs: backrs.spacing['spacing-2xl'], sm: backrs.spacing['spacing-4xl'] },
});
